import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin, Phone, ExternalLink, Briefcase, Mail, MessageCircle, FileText, Wallet, Gift, ListChecks, Instagram, User, Store, Tag } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { waLink, formatBRL, productContact, promoWhatsApp, isExpired } from "@/lib/site";
import SiteLayout from "@/components/site/SiteLayout";
import SmartImage from "@/components/site/SmartImage";

const Back = () => (
  <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-primary mb-4" data-testid="detail-back">
    <ArrowLeft className="w-4 h-4" /> Voltar
  </Link>
);

const useItem = (resource, id) => {
  const [item, setItem] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/${resource}/${id}`).then((r) => setItem(r.data)).catch(() => setItem(false));
  }, [resource, id]);
  return item;
};

export const NewsDetail = () => {
  const { id } = useParams();
  const n = useItem("news", id);
  if (!n) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;
  return (
    <SiteLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Back />
        <span className="text-xs font-semibold text-primary">{n.category} · {n.date}</span>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground mt-1 leading-tight">{n.title}</h1>
        {n.image && <img src={fileUrl(n.image)} alt={n.title} className="w-full h-56 sm:h-80 object-cover rounded-2xl my-4" />}
        <p className="text-base text-muted-foreground leading-relaxed whitespace-pre-line">{n.content || n.summary}</p>
      </article>
    </SiteLayout>
  );
};

export const EventDetail = () => {
  const { id } = useParams();
  const e = useItem("events", id);
  if (!e) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;
  return (
    <SiteLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Back />
        {e.image && <img src={fileUrl(e.image)} alt={e.name} className="w-full h-56 sm:h-80 object-cover rounded-2xl mb-4" />}
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground leading-tight">{e.name}</h1>
        <div className="mt-3 space-y-1.5 text-sm text-foreground">
          <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" /> {e.date}</div>
          {e.time && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> {e.time}</div>}
          {e.location && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> {e.location}</div>}
          {e.contact && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-primary" /> {e.contact}</div>}
        </div>
        <p className="text-base text-muted-foreground leading-relaxed mt-4 whitespace-pre-line">{e.description}</p>
      </article>
    </SiteLayout>
  );
};

export const TouristDetail = () => {
  const { id } = useParams();
  const t = useItem("tourist_spots", id);
  if (!t) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;
  const photos = t.photos && t.photos.length ? t.photos : [t.cover];
  return (
    <SiteLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Back />
        {t.cover && <img src={fileUrl(t.cover)} alt={t.name} className="w-full h-56 sm:h-80 object-cover rounded-2xl mb-4" />}
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground leading-tight">{t.name}</h1>
        {t.location && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" /> {t.location}</p>}
        <p className="text-base text-muted-foreground leading-relaxed mt-3 whitespace-pre-line">{t.description}</p>
        {t.map_link && (
          <a href={t.map_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 bg-primary text-primary-foreground font-semibold rounded-full px-5 py-2.5 text-sm" data-testid="tourist-map-link">
            <ExternalLink className="w-4 h-4" /> Ver localização
          </a>
        )}
        {photos.length > 1 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-5">
            {photos.map((p, i) => <img key={`${p}-${i}`} src={fileUrl(p)} alt="" className="w-full h-32 object-cover rounded-xl" />)}
          </div>
        )}
      </article>
    </SiteLayout>
  );
};


const InfoBlock = ({ icon: Icon, label, children }) => {
  if (!children) return null;
  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center gap-2 text-sm font-bold text-foreground font-heading mb-1">
        <Icon className="w-4 h-4 text-primary" /> {label}
      </div>
      <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{children}</div>
    </div>
  );
};

export const ServiceDetail = () => {
  const { id } = useParams();
  const s = useItem("services", id);
  if (!s) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;
  const waText = s.wa_button_text || "Falar pelo WhatsApp";
  return (
    <SiteLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="service-detail">
        <Back />
        {s.image && <img src={fileUrl(s.image)} alt={s.name} className="w-full h-52 sm:h-72 object-cover rounded-2xl mb-4" />}
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">{s.category}</span>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground leading-tight">{s.name}</h1>
        {s.professional && <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1"><User className="w-4 h-4 text-primary" /> {s.professional}</p>}
        <p className="text-base text-muted-foreground leading-relaxed mt-3 whitespace-pre-line">{s.description}</p>

        <div className="mt-4 space-y-1.5 text-sm text-foreground">
          {s.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary shrink-0" /> {s.address}</div>}
          {s.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-primary shrink-0" /> {s.phone}</div>}
          {s.hours && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary shrink-0" /> {s.hours}</div>}
          {s.price && <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-primary shrink-0" /> {s.price}</div>}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" data-testid="service-instagram" className="p-2.5 rounded-full bg-muted hover:bg-muted/70 transition-colors"><Instagram className="w-5 h-5 text-foreground" /></a>}
        </div>

        {s.whatsapp && (
          <a href={waLink(s.whatsapp, `Olá! Vi o serviço ${s.name} no Divulga Pocinhos Guia e gostaria de mais informações.`)} target="_blank" rel="noreferrer" data-testid="service-wa-detail" className="mt-4 w-full flex items-center justify-center gap-2 bg-secondary text-secondary-foreground font-bold rounded-full py-3 active:scale-95 transition-transform">
            <MessageCircle className="w-5 h-5" /> {waText}
          </a>
        )}
      </article>
    </SiteLayout>
  );
};

export const JobDetail = () => {
  const { id } = useParams();
  const j = useItem("jobs", id);
  if (!j) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;

  const waMsg = `Olá! Vi a vaga de ${j.title} no Divulga Pocinhos Guia e tenho interesse.`;
  const mailSubject = encodeURIComponent(`Candidatura - ${j.title}`);
  const mailBody = encodeURIComponent(`Olá! Vi a vaga de ${j.title} no Divulga Pocinhos Guia e gostaria de enviar meu currículo.`);

  return (
    <SiteLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="job-detail">
        <Back />
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0"><Briefcase className="w-6 h-6 text-primary" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground leading-tight">{j.title}</h1>
            {j.company && <p className="text-sm text-muted-foreground mt-0.5">{j.company}</p>}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-foreground">
          {j.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" /> {j.location}</span>}
          {j.contract_type && <span className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-primary" /> {j.contract_type}</span>}
          {j.schedule && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> {j.schedule}</span>}
          {j.salary && <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-primary" /> {j.salary}</span>}
          {j.date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary" /> {j.date}</span>}
        </div>

        <div className="mt-5 space-y-4">
          <InfoBlock icon={FileText} label="Descrição">{j.description}</InfoBlock>
          <InfoBlock icon={ListChecks} label="Requisitos">{j.requirements}</InfoBlock>
          <InfoBlock icon={Briefcase} label="Atividades">{j.activities}</InfoBlock>
          <InfoBlock icon={Gift} label="Benefícios">{j.benefits}</InfoBlock>
        </div>

        {(j.whatsapp || j.email) && (
          <div className="mt-6 border-t border-border pt-5">
            <h2 className="text-sm font-bold font-heading text-foreground mb-3">Entrar em contato</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              {j.whatsapp && (
                <a href={waLink(j.whatsapp, waMsg)} target="_blank" rel="noreferrer" data-testid="job-apply-whatsapp" className="flex-1 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground font-semibold rounded-full py-3 active:scale-95 transition-transform">
                  <MessageCircle className="w-5 h-5" /> Candidatar-se pelo WhatsApp
                </a>
              )}
              {j.email && (
                <a href={`mailto:${j.email}?subject=${mailSubject}&body=${mailBody}`} data-testid="job-apply-email" className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full py-3 active:scale-95 transition-transform">
                  <Mail className="w-5 h-5" /> Enviar currículo por e-mail
                </a>
              )}
            </div>
          </div>
        )}
      </article>
    </SiteLayout>
  );
};


export const ProductDetail = () => {
  const { id } = useParams();
  const p = useItem("products", id);
  const [store, setStore] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setActive(0);
    if (p && p.business_id) {
      api.get(`/public/businesses/${p.business_id}`).then((r) => setStore(r.data)).catch(() => setStore(null));
    }
  }, [p]);

  if (p === false) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Produto não encontrado.</div></SiteLayout>;
  if (!p) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;

  const gallery = [p.image, ...(Array.isArray(p.photos) ? p.photos : [])].filter(Boolean);
  const contact = productContact(p, store);
  const showContact = p.contact_enabled !== false && !!contact;
  const btnText = p.contact_button_text || "Falar com a loja";

  return (
    <SiteLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="product-detail">
        <Back />
        <div className="rounded-2xl overflow-hidden border border-border bg-card">
          <SmartImage src={gallery[active]} alt={p.name} className="w-full h-64 sm:h-96 object-cover" iconClassName="w-12 h-12" />
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 overflow-x-auto hide-scrollbar mt-3 pb-1">
            {gallery.map((g, i) => (
              <button key={`${g}-${i}`} type="button" onClick={() => setActive(i)} data-testid={`product-thumb-${i}`}
                className={`w-16 h-16 rounded-xl overflow-hidden shrink-0 border-2 transition-colors ${i === active ? "border-primary" : "border-transparent"}`}>
                <SmartImage src={g} alt="" className="w-full h-full object-cover" iconClassName="w-4 h-4" />
              </button>
            ))}
          </div>
        )}

        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground leading-tight mt-4">{p.name}</h1>
        {p.price !== undefined && p.price !== null && p.price !== "" && (
          <p className="text-2xl font-bold text-primary mt-1" data-testid="product-detail-price">{formatBRL(p.price)}</p>
        )}
        {p.available === false && (
          <span className="inline-block text-xs font-semibold text-muted-foreground bg-muted rounded-full px-3 py-1 mt-2">Indisponível</span>
        )}

        {store && (
          <Link to={`/empresa/${store.id}`} className="flex items-center gap-1.5 text-sm text-muted-foreground mt-3 hover:text-primary transition-colors" data-testid="product-store-link">
            <Store className="w-4 h-4 text-primary" /> Vendido por <span className="font-semibold text-foreground">{store.name}</span>
          </Link>
        )}

        {p.description && (
          <p className="text-base text-muted-foreground leading-relaxed mt-4 whitespace-pre-line">{p.description}</p>
        )}

        {showContact && (
          <a href={contact} target="_blank" rel="noreferrer" data-testid="product-contact-btn" className="mt-6 w-full flex items-center justify-center gap-2 bg-secondary text-secondary-foreground font-bold rounded-full py-3.5 active:scale-95 transition-transform">
            <MessageCircle className="w-5 h-5" /> {btnText}
          </a>
        )}
      </article>
    </SiteLayout>
  );
};


const Gallery = ({ images, alt }) => {
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [images.length]);
  return (
    <>
      <div className="rounded-2xl overflow-hidden border border-border bg-card">
        <SmartImage src={images[active]} alt={alt} className="w-full h-64 sm:h-96 object-cover" iconClassName="w-12 h-12" />
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mt-3 pb-1">
          {images.map((g, i) => (
            <button key={`${g}-${i}`} type="button" onClick={() => setActive(i)} data-testid={`promo-thumb-${i}`}
              className={`w-16 h-16 rounded-xl overflow-hidden shrink-0 border-2 transition-colors ${i === active ? "border-primary" : "border-transparent"}`}>
              <SmartImage src={g} alt="" className="w-full h-full object-cover" iconClassName="w-4 h-4" />
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export const PromotionDetail = () => {
  const { id } = useParams();
  const p = useItem("promotions", id);
  const [biz, setBiz] = useState(null);

  useEffect(() => {
    if (p && p.business_id) {
      api.get(`/public/businesses/${p.business_id}`).then((r) => setBiz(r.data)).catch(() => setBiz(null));
    }
  }, [p]);

  if (p === false) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Promoção não encontrada.</div></SiteLayout>;
  if (!p) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;

  const gallery = [p.image, ...(Array.isArray(p.photos) ? p.photos : [])].filter(Boolean);
  const oldP = parseFloat(p.old_price);
  const newP = parseFloat(p.new_price);
  const off = oldP > 0 && newP > 0 && newP < oldP ? Math.round((1 - newP / oldP) * 100) : 0;
  const wa = p.wa_enabled !== false ? promoWhatsApp(p, biz) : "";
  const expired = isExpired(p.valid_until);
  const validLabel = p.valid_until ? new Date(`${p.valid_until}T00:00:00`).toLocaleDateString("pt-BR") : "";

  return (
    <SiteLayout>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="promotion-detail">
        <Link to={biz ? `/empresa/${biz.id}` : "/"} className="inline-flex items-center gap-1 text-sm font-semibold text-primary mb-4" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <div className="relative">
          <Gallery images={gallery} alt={p.title} />
          {off > 0 && (
            <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-sm font-bold rounded-full px-3 py-1 shadow" data-testid="promo-detail-off">-{off}%</span>
          )}
        </div>

        <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-primary mt-4"><Tag className="w-3.5 h-3.5" /> Promoção</span>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading text-foreground leading-tight mt-1" data-testid="promo-detail-title">{p.title}</h1>

        <div className="flex items-end gap-3 mt-2">
          {p.old_price ? <span className="text-base text-muted-foreground line-through" data-testid="promo-detail-old-price">{formatBRL(p.old_price)}</span> : null}
          {p.new_price !== undefined && p.new_price !== "" && <span className="text-3xl font-bold text-primary font-heading" data-testid="promo-detail-price">{formatBRL(p.new_price)}</span>}
        </div>

        {validLabel && (
          <p className={`text-sm mt-2 flex items-center gap-1.5 ${expired ? "text-destructive" : "text-muted-foreground"}`} data-testid="promo-detail-valid">
            <Calendar className="w-4 h-4" /> {expired ? `Promoção encerrada em ${validLabel}` : `Válida até ${validLabel}`}
          </p>
        )}

        {biz && (
          <Link to={`/empresa/${biz.id}`} className="flex items-center gap-2 mt-4 p-3 rounded-2xl border border-border bg-card hover:border-primary transition-colors" data-testid="promo-detail-business">
            <SmartImage src={biz.logo || biz.cover} alt="" className="w-10 h-10 rounded-full object-cover" iconClassName="w-4 h-4" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Oferecido por</p>
              <p className="font-semibold text-foreground line-clamp-1">{biz.name}</p>
            </div>
          </Link>
        )}

        {p.description && <p className="text-base text-muted-foreground leading-relaxed mt-5 whitespace-pre-line" data-testid="promo-detail-description">{p.description}</p>}

        {wa && !expired && (
          <a href={wa} target="_blank" rel="noreferrer" data-testid="promo-detail-wa" className="mt-6 w-full flex items-center justify-center gap-2 bg-secondary text-secondary-foreground font-bold rounded-full py-3.5 active:scale-95 transition-transform">
            <MessageCircle className="w-5 h-5" /> {p.wa_button_text || "Falar com a empresa"}
          </a>
        )}
      </article>
    </SiteLayout>
  );
};
